import { useCallback, useLayoutEffect, useRef } from 'react';

import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { RxCross2 } from 'react-icons/rx';

import { useRegisterOpenModalAfterEnter } from './hooks';
import { type ModalProps, type SidebarModalProps } from './types';
import {
    APP_BOTTOM_CLEARANCE_CSS_VAR,
    getDefaultTransition,
    getSlideTransition,
    registerRightSidebarFollowerPanel,
} from './utils';
import { COMPONENTS_NAMESPACE } from '../../locales';
import { cn } from '../../utils/cn';

export const Modal: React.FC<ModalProps> = (props) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);
    const { children, initialFocusRef, onClose, onClosed, open, panelClassName, title } = props;
    const variant = props.variant ?? 'default';
    const side = variant === 'sidebar' ? (props as SidebarModalProps).side ?? 'right' : undefined;
    const width = variant === 'sidebar' ? (props as SidebarModalProps).width ?? '400px' : undefined;

    const isSidebar = variant === 'sidebar';
    const isRightSidebar = isSidebar && side === 'right' && Boolean(width);
    const isRightSidebarRef = useRef(isRightSidebar);
    isRightSidebarRef.current = isRightSidebar;

    const stopFollowerTrackingRef = useRef<(() => void) | null>(null);

    const setPanelRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (initialFocusRef) {
                initialFocusRef.current = node;
            }

            stopFollowerTrackingRef.current?.();
            stopFollowerTrackingRef.current = null;

            if (node && isRightSidebarRef.current) {
                stopFollowerTrackingRef.current = registerRightSidebarFollowerPanel(node);
            }
        },
        [initialFocusRef],
    );

    useLayoutEffect(() => {
        return () => {
            stopFollowerTrackingRef.current?.();
            stopFollowerTrackingRef.current = null;
        };
    }, []);

    const onAfterEnter = useRegisterOpenModalAfterEnter(open);

    const panelTransition = isSidebar ? getSlideTransition(side ?? 'right') : getDefaultTransition();

    const showHeading = Boolean(title);

    const renderCloseButton = (className: string) => (
        <button
            type="button"
            aria-label={t('modal.closeAriaLabel')}
            className={cn('cursor-pointer text-midnight opacity-80 transition-opacity hover:opacity-100', className)}
            onClick={onClose}
        >
            <RxCross2 size={18} aria-hidden="true" data-testid="modal-close-icon" />
        </button>
    );

    return (
        <Transition show={open} afterLeave={onClosed}>
            <Dialog as="div" onClose={onClose} initialFocus={initialFocusRef}>
                <TransitionChild
                    as="div"
                    enter="transition ease-out duration-150 transform"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition ease-out duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    className="fixed inset-0 bg-black bg-opacity-50 z-50"
                />
                <TransitionChild
                    as="div"
                    {...panelTransition}
                    afterEnter={onAfterEnter}
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                        'fixed z-50 max-h-full',
                        isSidebar
                            ? cn(
                                  'inset-x-0 top-0 flex overflow-hidden',
                                  side === 'left' ? 'justify-start' : 'justify-end',
                              )
                            : 'inset-0 flex min-h-full transform items-center justify-center overflow-y-auto px-4 py-4 lg:px-6',
                    )}
                    style={isSidebar ? { bottom: `var(${APP_BOTTOM_CLEARANCE_CSS_VAR}, 0px)` } : undefined}
                    data-testid="modal-container"
                >
                    <DialogPanel
                        as="div"
                        className={cn(
                            'relative flex min-h-0 flex-col overflow-hidden border border-[#e7ecf3] bg-white shadow-[0px_2px_6px_0px_rgba(163,177,196,0.2)]',
                            isSidebar ? 'h-full' : 'max-h-[90%] w-full max-w-[832px] rounded-lg',
                            panelClassName,
                        )}
                        style={isSidebar ? { width } : undefined}
                        ref={setPanelRef}
                        data-testid="modal-panel"
                    >
                        {showHeading ? (
                            // Close icon sits in the title row so it is vertically centred with the title.
                            <div className="flex shrink-0 items-center justify-between gap-4 pb-4 pl-6 pr-3.5 pt-6">
                                <DialogTitle as="h6" className="text-base font-bold text-midnight">
                                    {title}
                                </DialogTitle>
                                {renderCloseButton('shrink-0')}
                            </div>
                        ) : (
                            renderCloseButton('absolute right-3.5 top-3.5 z-10')
                        )}
                        <div
                            className={cn(
                                'min-h-0 flex-1 overflow-y-auto px-6 pb-6',
                                'scrollbar-thin scrollbar-thumb-rounded-md scrollbar-thumb-dark scrollbar-track-rounded-md scrollbar-track-transparent',
                                showHeading ? 'pt-0' : 'pt-6',
                            )}
                            data-testid="modal-body"
                        >
                            {children}
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </Dialog>
        </Transition>
    );
};
